"use client";

import { RefObject, useEffect, useState } from "react";

import { DARK_BAND_ATTRIBUTE } from "@/app/(marketing)/constants";

interface BandReport {
  pathname: string;
  isOverDarkBand: boolean;
  isSettled: boolean;
}

export interface HeaderBand {
  /** Whether a dark band is under the header's bottom edge. `null` until this
      page's bands first report, and on a page with none, so the header keeps
      what the server rendered. */
  isOverDarkBand: boolean | null;
  /** True from this page's second report on, once the header has taken the
      page's own answer. The header fades its tone only then: a change after
      that is a band's edge scrolling past, while the first report may only be
      correcting the server's guess, and a correction should snap. */
  isSettled: boolean;
}

/**
 * Whether a dark band sits under the header's bottom edge, which is what a
 * reader sees the header against. The observer's root is the 1px line just
 * above that edge, measured from the header itself, so each band reports as it
 * crosses the line and nothing listens to scroll. The line's bottom margin
 * depends on the viewport's height, so the observer is rebuilt on resize, and a
 * rebuild that reports the same answer does not re-render the header.
 *
 * Bands are found once per pathname, when the page mounts; a band mounted later
 * is not watched.
 *
 * Leaving a page clears the report, so coming back to the homepage renders its
 * header dark at once rather than from the answer it had when the reader left.
 * The report also carries its pathname, so a page could not render with another
 * page's answer even if that clear landed a frame late; in the browser it lands
 * first.
 */
export function useHeaderOverDarkBand(
  pathname: string,
  headerRef: RefObject<HTMLElement | null>,
): HeaderBand {
  const [report, setReport] = useState<BandReport | null>(null);

  useEffect(() => {
    const header = headerRef.current;
    const bands = document.querySelectorAll(`[${DARK_BAND_ATTRIBUTE}]`);

    if (!header || bands.length === 0) {
      return;
    }

    const bandsOnLine = new Set<Element>();
    let observer: IntersectionObserver | null = null;

    function observe(pinnedHeader: HTMLElement) {
      observer?.disconnect();
      bandsOnLine.clear();

      // The header is pinned to the top of the viewport, so its bottom edge
      // is where the page shows below it.
      const edge = Math.round(pinnedHeader.getBoundingClientRect().bottom);

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              bandsOnLine.add(entry.target);
            } else {
              bandsOnLine.delete(entry.target);
            }
          }

          const isOverDarkBand = bandsOnLine.size > 0;

          setReport((previous) => {
            const isSettled = previous?.pathname === pathname;

            if (isSettled && previous.isSettled && previous.isOverDarkBand === isOverDarkBand) {
              return previous;
            }

            return { pathname, isOverDarkBand, isSettled };
          });
        },
        { rootMargin: `-${edge - 1}px 0px -${window.innerHeight - edge}px 0px` },
      );

      bands.forEach((band) => observer?.observe(band));
    }

    const rebuild = () => observe(header);

    rebuild();
    window.addEventListener("resize", rebuild);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", rebuild);
      setReport(null);
    };
  }, [pathname, headerRef]);

  const current = report?.pathname === pathname ? report : null;

  return {
    isOverDarkBand: current ? current.isOverDarkBand : null,
    isSettled: current ? current.isSettled : false,
  };
}
